import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import type { BotSettings } from "../types";
import { tg } from "../telegram";
import { useT } from "../i18n";

const INTERVAL_MIN = 5, INTERVAL_MAX = 180;

export default function Settings() {
  const { t } = useT();
  const [loaded, setLoaded] = useState<BotSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [interval, setIntervalVal] = useState(30);

  useEffect(() => {
    api.get<BotSettings>("/api/settings")
      .then(s => {
        setLoaded(s);
        setIntervalVal(s.notification_interval_min);
      })
      .catch((e: ApiError) => setError(e.message));
  }, []);

  const valid = interval >= INTERVAL_MIN && interval <= INTERVAL_MAX;
  const dirty = !loaded ? false : interval !== loaded.notification_interval_min;

  useEffect(() => {
    if (!success) return;
    const id = setTimeout(() => setSuccess(false), 2500);
    return () => clearTimeout(id);
  }, [success]);

  const save = async () => {
    if (!valid) return;
    setError(null); setSuccess(false);
    try {
      const r = await api.put<BotSettings>("/api/settings", {
        notification_interval_min: interval,
      });
      setLoaded(r);
      setSuccess(true);
      tg()?.HapticFeedback?.notificationOccurred("success");
    } catch (e) {
      tg()?.HapticFeedback?.notificationOccurred("error");
      setError((e as ApiError).message);
    }
  };

  return (
    <div>
      <h1>{t("settings.title")}</h1>
      {error && <div className="alert error">{error}</div>}
      {success && <div className="alert success">✅ {t("common.saved")}</div>}

      {!loaded && !error && (
        <div className="card skeleton" style={{ height: 100 }} />
      )}

      {loaded && (
        <div className="card">
          <label style={{ display: "block", marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: "var(--hint)", marginBottom: 4 }}>
              {t("settings.notification_interval", { min: INTERVAL_MIN, max: INTERVAL_MAX })}
            </div>
            <input
              type="number"
              min={INTERVAL_MIN}
              max={INTERVAL_MAX}
              step={5}
              value={interval}
              onChange={e => setIntervalVal(parseInt(e.target.value) || 0)}
            />
            {!valid && (
              <div style={{ fontSize: 11, color: "var(--destructive)", marginTop: 4 }}>
                {t("settings.range_error", { min: INTERVAL_MIN, max: INTERVAL_MAX })}
              </div>
            )}
            <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 4 }}>
              {t("settings.notification_hint", { n: interval })}
            </div>
          </label>
          <button className="btn" onClick={save} disabled={!valid || !dirty}>
            {dirty ? t("common.save") : t("common.no_changes")}
          </button>
        </div>
      )}
    </div>
  );
}
